import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const translations = {
    en: {
        step0: {
            title: "What would you like us to build?",
            subtitle: "Choose what best fits your project"
        },
        step1: {
            title: "What type of project?",
            subtitle: "Tell us more about what you need"
        },
        step2: {
            title: "Which scope fits best?",
            subtitle: "This gives us a baseline for the estimate"
        },
        step3: {
            title: "Content & Management",
            subtitle: "How would you like to handle your site?",
            q1: "1. What type of content do you need?",
            q2: "2. Do you wish to edit it yourself?"
        },
        step4: {
            title: "Technical Details",
            subtitle: "A few final details for the quote",
            q1: "3. Do you need multiple languages?",
            q2base: "4. Domain & Hosting",
            q2mobile: "4. Publishing Strategy",
            q3: "5. Interested in continuous support?"
        },
        step5: {
            title: "Describe your idea",
            subtitle: "Optional details help us understand better",
            placeholder: "Tell us about the features, style, or functionality you have in mind...",
            skip: "Skip this step"
        },
        step6: {
            title: "Almost there!",
            subtitle: "Where should we send the detailed quote?",
            businessLabel: "Your business name *",
            businessPlaceholder: "e.g. Acme Inc.",
            emailLabel: "Your email (optional)",
            emailPlaceholder: "you@company.com",
            disclaimer: (type) => `We'll review your ${type === 'website' ? 'website' : 'project'} requirements and send a custom proposal.`
        },
        result: {
            title: "Estimate Ready!",
            subtitle: (name, project) => <>Great news {name}! Based on your needs for a <strong className="text-text-primary">{project}</strong>, here is the breakdown.</>,
            mobileTitle: "Estimated Investment",
            bookBtn: "Book Free Consultation",
            startOver: "Start Over"
        },
        nav: {
            start: "Start",
            step: "Step",
            complete: "Complete",
            back: "Back",
            continue: "Continue",
            seeEstimate: "See My Estimate"
        },
        panel: {
            title: "Your Quote",
            draft: "Draft",
            liveUpdates: "Live Updates",
            estInvestment: "Estimated Investment",
            basedOn: "Based on typical project scope",
            projectDetails: "Project Details",
            projectType: "Type",
            scope: "Scope",
            setup: "Setup",
            features: "Features",
            notSelected: "Not selected",
            pending: "Pending...",
            needHelp: "Need help choosing?",
            contactUs: "Contact us",
            contactText: "Our team can help you define your project requirements."
        },
        mobileBar: {
            estCost: "Estimated Cost",
            live: "Live",
            selectOptions: "Select options..."
        },
        options: {
            website: "Website",
            mobile: "Mobile App",
            ecommerce: "Online Shop",
            other: "Something Else",

            webapp: "Web Application",
            saas: "SaaS Product",
            ai: "AI / Automation",
            api: "Connect Systems",
            software: "Custom Software",

            landing: "Landing Page",
            landingDesc: "Simple page to showcase your business",
            business: "Business Website",
            businessDesc: "Multiple pages with company info",
            portfolio: "Portfolio",
            portfolioDesc: "Show off your work",
            blog: "Blog / Content Site",
            blogDesc: "Regular content updates",

            mvp: "Simple App (MVP)",
            mvpDesc: "Core features to test your idea",
            standard: "Full App",
            standardDesc: "Complete app with all features",
            complex: "Complex App",
            complexDesc: "Advanced features, multiple integrations",

            smallShop: "Small Shop",
            smallShopDesc: "Under 50 products",
            mediumShop: "Medium Shop",
            mediumShopDesc: "50-500 products",
            largeShop: "Large Store",
            largeShopDesc: "500+ products, advanced features",

            simpleTool: "Simple Tool",
            simpleToolDesc: "One main feature or function",
            dashboard: "Dashboard / Admin",
            dashboardDesc: "Data views and management",
            platform: "Full Platform",
            platformDesc: "Multiple user types, complex features",

            smallProj: "Small Project",
            smallProjDesc: "Simple scope, quick turnaround",
            mediumProj: "Medium Project",
            mediumProjDesc: "Standard complexity",
            largeProj: "Large Project",
            largeProjDesc: "Complex requirements",

            useContent: "Use my content",
            useContentDesc: "I provide all text & images",
            helpContent: "Help me write it",
            helpContentDesc: "I need professional assistance",
            createContent: "Create it for me",
            createContentDesc: "Full copywriting & assets",

            editSelf: "I edit myself",
            editSelfDesc: "Full control anytime",
            youHandle: "You handle updates",
            youHandleDesc: "We manage everything",
            hybrid: "Hybrid",
            hybridDesc: "I do small edits, you do big ones",

            singleLang: "Single Language",
            multiLang: "Multiple",
            futureLang: "Maybe Later",

            contSupport: "Continuous Support",
            contSupportDesc: "Hourly support when needed",
            noSupport: "No Support",
            noSupportDesc: "I handle it myself",
            maybeSupport: "Maybe Later",
            maybeSupportDesc: "Decide in the future",

            publish: "Publish to Stores",
            publishDesc: "Apple & Google Play",
            justCode: "Just Code",
            justCodeDesc: "I publish myself",

            domainHost: "Domain & Hosting",
            domainHostDesc: "Full setup",
            justHost: "Just Hosting",
            justHostDesc: "I have a domain",
            justDomain: "Just Domain",
            justDomainDesc: "I have hosting",
            neither: "Neither",
            neitherDesc: "I have both",

            supportPlan: "Support Plan"
        }
    },
    dk: {
        step0: {
            title: "Hvad vil du have os til at bygge?",
            subtitle: "Vælg det der passer bedst til dit projekt"
        },
        step1: {
            title: "Hvilken type projekt?",
            subtitle: "Fortæl os mere om hvad du har brug for"
        },
        step2: {
            title: "Hvilket omfang passer bedst?",
            subtitle: "Dette giver os et udgangspunkt for estimatet"
        },
        step3: {
            title: "Indhold & Vedligeholdelse",
            subtitle: "Hvordan vil du håndtere din side?",
            q1: "1. Hvilken type indhold har du brug for?",
            q2: "2. Ønsker du selv at redigere siden?"
        },
        step4: {
            title: "Tekniske Detaljer",
            subtitle: "Et par sidste detaljer til tilbuddet",
            q1: "3. Har du brug for flere sprog?",
            q2base: "4. Domæne & Hosting",
            q2mobile: "4. Udgivelsesstrategi",
            q3: "5. Interesseret i løbende support?"
        },
        step5: {
            title: "Beskriv din idé",
            subtitle: "Valgfrie detaljer hjælper os med at forstå bedre",
            placeholder: "Fortæl os om funktioner, stil eller funktionalitet du har i tankerne...",
            skip: "Spring dette trin over"
        },
        step6: {
            title: "Næsten i mål!",
            subtitle: "Hvor skal vi sende det detaljerede tilbud?",
            businessLabel: "Dit virksomhedsnavn *",
            businessPlaceholder: "f.eks. Acme ApS",
            emailLabel: "Din email (valgfri)",
            emailPlaceholder: "dig@virksomhed.dk",
            disclaimer: (type) => `Vi gennemgår dine krav til din ${type === 'website' ? 'hjemmeside' : 'projekt'} og sender et skræddersyet tilbud.`
        },
        result: {
            title: "Estimat Klar!",
            subtitle: (name, project) => <>Godt nyt {name}! Baseret på dine behov for en <strong className="text-text-primary">{project}</strong>, er her overblikket.</>,
            mobileTitle: "Estimeret Investering",
            bookBtn: "Book Gratis Konsultation",
            startOver: "Start Forfra"
        },
        nav: {
            start: "Start",
            step: "Trin",
            complete: "Færdig",
            back: "Tilbage",
            continue: "Fortsæt",
            seeEstimate: "Se Mit Estimat"
        },
        panel: {
            title: "Dit Tilbud",
            draft: "Kladde",
            liveUpdates: "Live Opdateringer",
            estInvestment: "Estimeret Investering",
            basedOn: "Baseret på typisk projektomfang",
            projectDetails: "Projektdetaljer",
            projectType: "Type",
            scope: "Omfang",
            setup: "Opsætning",
            features: "Funktioner",
            notSelected: "Ikke valgt",
            pending: "Venter...",
            needHelp: "Brug for hjælp?",
            contactUs: "Kontakt os",
            contactText: "Vores team kan hjælpe dig med at definere dine krav."
        },
        mobileBar: {
            estCost: "Estimeret Pris",
            live: "Live",
            selectOptions: "Vælg muligheder..."
        },
        options: {
            website: "Hjemmeside",
            mobile: "Mobil App",
            ecommerce: "Webshop",
            other: "Noget Andet",

            webapp: "Web Applikation",
            saas: "SaaS Produkt",
            ai: "AI / Automatisering",
            api: "System Integration",
            software: "Specialsoftware",

            landing: "Landing Page",
            landingDesc: "Simpel side til at vise din virksomhed",
            business: "Virksomhedsside",
            businessDesc: "Flere sider med information",
            portfolio: "Portefølje",
            portfolioDesc: "Vis dit arbejde frem",
            blog: "Blog / Indholdsside",
            blogDesc: "Jævnlige opdateringer",

            mvp: "Simpel App (MVP)",
            mvpDesc: "Kernefunktioner til at teste din idé",
            standard: "Fuld App",
            standardDesc: "Komplet app med alle funktioner",
            complex: "Kompleks App",
            complexDesc: "Avancerede funktioner, integrationer",

            smallShop: "Lille Shop",
            smallShopDesc: "Under 50 produkter",
            mediumShop: "Mellem Shop",
            mediumShopDesc: "50-500 produkter",
            largeShop: "Stor Shop",
            largeShopDesc: "500+ produkter, avancerede funktioner",

            simpleTool: "Simpelt Værktøj",
            simpleToolDesc: "En hovedfunktion",
            dashboard: "Dashboard / Admin",
            dashboardDesc: "Datavisning og håndtering",
            platform: "Fuld Platform",
            platformDesc: "Flere brugertyper, komplekse funktioner",

            smallProj: "Lille Projekt",
            smallProjDesc: "Simpelt omfang, hurtig levering",
            mediumProj: "Mellem Projekt",
            mediumProjDesc: "Standard kompleksitet",
            largeProj: "Stort Projekt",
            largeProjDesc: "Komplekse krav",

            useContent: "Bruger mit indhold",
            useContentDesc: "Jeg leverer tekst & billeder",
            helpContent: "Hjælp mig med det",
            helpContentDesc: "Jeg har brug for professionel hjælp",
            createContent: "Lav det hele for mig",
            createContentDesc: "Fuld tekstforfatning & materialer",

            editSelf: "Jeg redigerer selv",
            editSelfDesc: "Fuld kontrol altid",
            youHandle: "I står for det",
            youHandleDesc: "Vi står for alt vedligehold",
            hybrid: "Hybrid",
            hybridDesc: "Jeg tager små ting, I tager store",

            singleLang: "Et Sprog",
            multiLang: "Flere Sprog",
            futureLang: "Måske Senere",

            contSupport: "Løbende Support",
            contSupportDesc: "Timesupport efter behov",
            noSupport: "Ingen Support",
            noSupportDesc: "Jeg klarer det selv",
            maybeSupport: "Måske Senere",
            maybeSupportDesc: "Beslutter i fremtiden",

            publish: "Udgiv til Stores",
            publishDesc: "Apple & Google Play",
            justCode: "Kun Koden",
            justCodeDesc: "Jeg udgiver selv",

            domainHost: "Domæne & Hosting",
            domainHostDesc: "Fuld opsætning",
            justHost: "Kun Hosting",
            justHostDesc: "Jeg har et domæne",
            justDomain: "Kun Domæne",
            justDomainDesc: "Jeg har hosting",
            neither: "Hverken eller",
            neitherDesc: "Jeg har begge dele",

            supportPlan: "Supportaftale"
        }
    }
};

const PriceCalculator = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({
        projectType: null,
        purpose: null,
        // New fields
        content: null,      // 'self', 'help', 'full'
        management: null,   // 'self', 'service', 'hybrid'
        language: null,     // 'single', 'multi', 'future'
        hosting: null,      // 'domain', 'hosting', 'both', 'neither' (or 'publish' for apps)
        support: null,      // 'yes', 'no', 'maybe'
        description: '',    // string
        // Info
        businessName: '',
        email: '',
    });
    const navigate = useNavigate();
    const { lang = 'en' } = useParams();

    // Helper to get text based on current language
    // Default to 'dk' if lang is 'dk' or 'da', otherwise 'en'
    const currentLang = ['dk', 'da'].includes(lang) ? 'dk' : 'en';
    const t = translations[currentLang];

    // Scroll to top on step change
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    // --- Options Data ---

    // --- Icons ---
    const Icons = {
        Website: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.546-3.131 1.457-4.371" /></svg>,
        Mobile: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" /></svg>,
        Ecommerce: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 5.059a2.25 2.25 0 01-.036 1.768l-1.87 5.61A2.25 2.25 0 0116.89 22.5H7.11a2.25 2.25 0 01-2.126-1.556l-1.87-5.61a2.25 2.25 0 01-.036-1.768l1.262-5.06a2.25 2.25 0 012.183-1.706h8.773a2.25 2.25 0 012.183 1.706z" /></svg>,
        Other: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></svg>,
        Webapp: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>,
        SaaS: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" /></svg>,
        AI: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" /></svg>, // Reusing sparkles for AI concept
        API: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>,
        Software: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.85 6.361a15.996 15.996 0 00-4.647 4.763m0 0a15.998 15.998 0 01-1.622 3.395m3.42-3.42a15.996 15.996 0 00-1.62-3.379m3.38 1.62a15.995 15.995 0 011.62 3.379m-3.38-1.62c.926-.926 2-1.78 3.19-2.52" /></svg>,
        Content: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>,
        Help: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575V12a6.75 6.75 0 006.75 6.75h.75M6.9 7.575a1.575 1.575 0 00-3.15 0v8.175a6.75 6.75 0 006.75 6.75h3.6" /></svg>, // Hand gesture similar to handshake
        Edit: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>,
        Service: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>,
        Hybrid: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A5.99 5.99 0 0112 4.5c-2.31 0-4.239.943-5.94 2.47m0 0A5.99 5.99 0 0112 20.25a5.99 5.99 0 015.94-13.28m0 0c2.31 0 4.239.943 5.94 2.47m0 0A5.99 5.99 0 0112 20.25" /></svg>, // Scale-like
        LangSingle: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>, // Using phone as single contact metaphor (simplified)
        LangMulti: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.546-3.131 1.457-4.371" /></svg>,
        Time: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
        SupportYes: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>,
        SupportNo: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
        SupportMaybe: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg>,
        Publish: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" /></svg>,
        Code: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>,
        Hosting: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.07 0 1.95.546 2.475 1.378l2.636 4.379A4.5 4.5 0 0121.75 12" /></svg>,
        Domain: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5l-3.9 19.5m-2.1-19.5l-3.9 19.5" /></svg>,
        Both: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>,
        Neither: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>,
        ArrowRight: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>,
        Lightbulb: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.854 1.591-2.073a6.002 6.002 0 005.01-5.127A5.996 5.996 0 0012 4.5a5.996 5.996 0 00-6.852 6.108 5.998 5.998 0 004.26 5.077c.933.219 1.591 1.09 1.591 2.073V18m3.75 0h-3.75" /></svg>,
        Clipboard: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" /></svg>,
        Target: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zm-7.518-.267A8.25 8.25 0 1120.25 10.5M8.288 14.212A5.25 5.25 0 1117.25 10.5" /></svg>,
        Party: () => <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 17V17.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M17 17V17.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M7 17V17.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M16 7V7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M8 7V7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    };

    const projectTypes = [
        { id: 'website', label: t.options.website, icon: <Icons.Website /> },
        { id: 'mobile', label: t.options.mobile, icon: <Icons.Mobile /> },
        { id: 'ecommerce', label: t.options.ecommerce, icon: <Icons.Ecommerce /> },
        { id: 'other', label: t.options.other, icon: <Icons.Other /> },
    ];

    const otherOptions = [
        { id: 'webapp', label: t.options.webapp, icon: <Icons.Webapp /> },
        { id: 'saas', label: t.options.saas, icon: <Icons.SaaS /> },
        { id: 'ai', label: t.options.ai, icon: <Icons.AI /> },
        { id: 'api', label: t.options.api, icon: <Icons.API /> },
        { id: 'software', label: t.options.software, icon: <Icons.Software /> },
    ];

    const contentOptions = [
        { id: 'self', label: t.options.useContent, description: t.options.useContentDesc, icon: <Icons.Content /> },
        { id: 'help', label: t.options.helpContent, description: t.options.helpContentDesc, icon: <Icons.Help /> },
        { id: 'full', label: t.options.createContent, description: t.options.createContentDesc, icon: <Icons.Other /> },
    ];

    const managementOptions = [
        { id: 'self', label: t.options.editSelf, description: t.options.editSelfDesc, icon: <Icons.Edit /> },
        { id: 'service', label: t.options.youHandle, description: t.options.youHandleDesc, icon: <Icons.Service /> },
        { id: 'hybrid', label: t.options.hybrid, description: t.options.hybridDesc, icon: <Icons.Hybrid /> },
    ];

    const languageOptions = [
        { id: 'single', label: t.options.singleLang, icon: <Icons.LangSingle /> },
        { id: 'multi', label: t.options.multiLang, icon: <Icons.LangMulti /> },
        { id: 'future', label: t.options.futureLang, icon: <Icons.Time /> },
    ];

    const supportOptions = [
        { id: 'yes', label: t.options.contSupport, description: t.options.contSupportDesc, icon: <Icons.SupportYes /> },
        { id: 'no', label: t.options.noSupport, description: t.options.noSupportDesc, icon: <Icons.SupportNo /> },
        { id: 'maybe', label: t.options.maybeSupport, description: t.options.maybeSupportDesc, icon: <Icons.SupportMaybe /> },
    ];

    const getHostingOptions = (type) => {
        if (type === 'mobile') {
            return [
                { id: 'publish', label: t.options.publish, description: t.options.publishDesc, icon: <Icons.Publish /> },
                { id: 'code', label: t.options.justCode, description: t.options.justCodeDesc, icon: <Icons.Code /> },
            ];
        }
        return [
            { id: 'both', label: t.options.domainHost, description: t.options.domainHostDesc, icon: <Icons.Both /> },
            { id: 'hosting', label: t.options.justHost, description: t.options.justHostDesc, icon: <Icons.Hosting /> },
            { id: 'domain', label: t.options.justDomain, description: t.options.justDomainDesc, icon: <Icons.Domain /> },
            { id: 'neither', label: t.options.neither, description: t.options.neitherDesc, icon: <Icons.Neither /> },
        ];
    };

    const getPurposeOptions = () => {
        const type = answers.projectType;

        if (type === 'website') {
            return [
                { id: 'landing', label: t.options.landing, description: t.options.landingDesc },
                { id: 'business', label: t.options.business, description: t.options.businessDesc },
                { id: 'portfolio', label: t.options.portfolio, description: t.options.portfolioDesc },
                { id: 'blog', label: t.options.blog, description: t.options.blogDesc },
            ];
        }

        if (type === 'mobile') {
            return [
                { id: 'mvp', label: t.options.mvp, description: t.options.mvpDesc },
                { id: 'standard', label: t.options.standard, description: t.options.standardDesc },
                { id: 'complex', label: t.options.complex, description: t.options.complexDesc },
            ];
        }

        if (type === 'ecommerce') {
            return [
                { id: 'small', label: t.options.smallShop, description: t.options.smallShopDesc },
                { id: 'medium', label: t.options.mediumShop, description: t.options.mediumShopDesc },
                { id: 'large', label: t.options.largeShop, description: t.options.largeShopDesc },
            ];
        }

        if (type === 'webapp') {
            return [
                { id: 'simple', label: t.options.simpleTool, description: t.options.simpleToolDesc },
                { id: 'dashboard', label: t.options.dashboard, description: t.options.dashboardDesc },
                { id: 'platform', label: t.options.platform, description: t.options.platformDesc },
            ];
        }

        return [
            { id: 'small', label: t.options.smallProj, description: t.options.smallProjDesc },
            { id: 'medium', label: t.options.mediumProj, description: t.options.mediumProjDesc },
            { id: 'large', label: t.options.largeProj, description: t.options.largeProjDesc },
        ];
    };

    // --- Logic ---

    const calculatePrice = () => {
        const basePrices = {
            website: { landing: 3000, business: 6000, portfolio: 4000, blog: 5000, _default: 4500 },
            mobile: { mvp: 7000, standard: 15000, complex: 26000, _default: 15000 },
            ecommerce: { small: 6000, medium: 12000, large: 20000, _default: 12000 },
            webapp: { simple: 5000, dashboard: 10000, platform: 18000, _default: 10000 },
            saas: { small: 12000, medium: 20000, large: 35000, _default: 20000 },
            ai: { small: 8000, medium: 15000, large: 25000, _default: 15000 },
            api: { small: 4000, medium: 8000, large: 15000, _default: 8000 },
            software: { small: 10000, medium: 18000, large: 30000, _default: 18000 },
        };

        if (!answers.projectType || answers.projectType === 'other') {
            return null;
        }

        const projectPrices = basePrices[answers.projectType];

        // Base Price Calculation
        let baseLow = 0;
        let baseHigh = 0;
        let isPrecise = false;

        if (answers.purpose && projectPrices[answers.purpose]) {
            baseLow = projectPrices[answers.purpose];
            baseHigh = projectPrices[answers.purpose];
            isPrecise = true;
        } else {
            // Range if purpose not selected: use Min and Max of available options
            const prices = Object.entries(projectPrices)
                .filter(([key]) => key !== '_default')
                .map(([, value]) => value);
            baseLow = Math.min(...prices);
            baseHigh = Math.max(...prices);
        }

        // Add-ons Logic
        let addOnFixed = 0;
        let multiplier = 1;

        if (answers.content === 'help') addOnFixed += 3000;
        if (answers.content === 'full') addOnFixed += 8000;

        if (answers.language === 'multi') multiplier *= 1.25;
        if (answers.language === 'future') multiplier *= 1.05;

        if (answers.hosting === 'both') addOnFixed += 1500;
        if (answers.hosting === 'publish' && answers.projectType === 'mobile') addOnFixed += 2500;

        // Apply Add-ons
        baseLow = (baseLow + addOnFixed) * multiplier;
        baseHigh = (baseHigh + addOnFixed) * multiplier;

        // Final Ranges
        // Always use 0.15 volatility to give a realistic range around the base points
        // If not precise, it effectively stretches from (Min - 15%) to (Max + 15%)
        const volatility = 0.15;

        const low = Math.round(baseLow * (1 - volatility) / 100) * 100;
        const high = Math.round(baseHigh * (1 + volatility) / 100) * 100;

        return {
            low,
            high,
            isEstimate: !isPrecise || step < 3 // Still an estimate if we haven't finished logistics
        };
    };

    const handleSelect = (field, value) => {
        if (field === 'projectType' && value !== answers.projectType) {
            // Reset downstream fields
            setAnswers({ ...answers, [field]: value, purpose: null });
        } else {
            setAnswers({ ...answers, [field]: value });
        }
    };

    // --- Navigation ---

    const nextStep = () => {
        if (step === 0 && answers.projectType && answers.projectType !== 'other') {
            setStep(2); // Skip Other sub-selection if not 'other'
        } else {
            setStep(step + 1);
        }
    };

    const prevStep = () => {
        if (step === 2 && !['saas', 'ai', 'api', 'software', 'webapp'].includes(answers.projectType) && answers.projectType !== 'other') {
            setStep(0);
        } else {
            setStep(step - 1);
        }
    };

    const canProceed = () => {
        if (step === 0) return answers.projectType !== null;
        if (step === 1) return answers.projectType !== null && answers.projectType !== 'other'; // Sub-type
        if (step === 2) return answers.purpose !== null; // Purpose
        if (step === 3) return answers.content !== null && (answers.projectType === 'mobile' ? true : answers.management !== null); // Content & Mgmt (Mgmt skipped for mobile)
        if (step === 4) return answers.language !== null && answers.hosting !== null && answers.support !== null; // Logistics
        if (step === 5) return true; // Description is optional
        if (step === 6) return answers.businessName.trim() !== ''; // Contact
        return true;
    };

    const getStepInfo = () => {
        // Simplified mapping for progress bar logic
        if (step === 0 || step === 1) return { current: 1, total: 6 };
        if (step === 2) return { current: 2, total: 6 };
        if (step === 3) return { current: 3, total: 6 };
        if (step === 4) return { current: 4, total: 6 };
        if (step === 5) return { current: 5, total: 6 };
        if (step >= 6) return { current: 6, total: 6 };
        return { current: 1, total: 6 };
    };

    const stepInfo = getStepInfo();

    // Utility
    const getSelectedProject = () => {
        return projectTypes.find(p => p.id === answers.projectType) ||
            otherOptions.find(p => p.id === answers.projectType);
    };

    // --- Components ---

    const OptionCard = ({ option, selected, onClick, showDescription = false, compact = false }) => (
        <button
            onClick={onClick}
            className={`relative rounded-xl border-2 text-left transition-colors duration-200 w-full ${compact ? 'p-3' : 'p-4'} ${selected
                ? 'border-secondary-accent bg-secondary-accent/5'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
        >
            <div className="flex items-center gap-3">
                <span className={`${compact ? 'text-xl' : 'text-2xl'} flex-shrink-0`}>{option.icon}</span>
                <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold ${compact ? 'text-sm' : 'text-base'} ${selected ? 'text-secondary-accent' : 'text-text-primary'}`}>
                        {option.label}
                    </h3>
                    {showDescription && option.description && (
                        <p className="text-grey-accent text-xs mt-0.5">{option.description}</p>
                    )}
                </div>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${selected ? 'bg-secondary-accent' : 'border-2 border-gray-200'
                    }`}>
                    {selected && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </div>
            </div>
        </button>
    );

    const renderEstimatePanel = (isMobile = false) => {
        const price = calculatePrice();
        const selectedProject = getSelectedProject();
        const purposeOptions = getPurposeOptions();
        const selectedPurpose = purposeOptions.find(p => p.id === answers.purpose);

        // Mobile Sticky Bar
        if (isMobile) {
            return (
                <div
                    className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-40 lg:hidden"
                >
                    <div className="px-5 py-4 safe-area-bottom">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-secondary-accent/10 flex items-center justify-center text-xl">
                                    {selectedProject?.icon || <Icons.Lightbulb />}
                                </div>
                                <div className="flex flex-col justify-center">
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-grey-accent mb-0.5">{t.mobileBar.estCost}</p>
                                    <div className="h-6 flex items-center">
                                        {price ? (
                                            <p className="font-bold text-text-primary text-lg leading-none">
                                                {price.low.toLocaleString()} – {price.high.toLocaleString()} <span className="text-xs font-normal text-grey-accent">DKK</span>
                                            </p>
                                        ) : (
                                            <p className="text-sm text-grey-accent font-medium">
                                                {t.mobileBar.selectOptions}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {price && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                                    {t.mobileBar.live}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // Desktop Sidebar
        return (
            <div className="h-full flex flex-col bg-[#FAFAFA] border-l border-[#EDEDED] p-8 xl:p-10">
                <div className="flex items-center justify-between mb-8">
                    <h3 className="font-bold text-xl text-text-primary">{t.panel.title}</h3>
                    {price ? (
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wide">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            {t.panel.liveUpdates}
                        </div>
                    ) : (
                        <span className="text-xs font-medium text-grey-accent uppercase tracking-wider">{t.panel.draft}</span>
                    )}
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#EDEDED] mb-8 relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1 h-full bg-secondary-accent"></div>
                    <p className="text-xs font-bold text-grey-accent uppercase tracking-wider mb-2">{t.panel.estInvestment}</p>

                    <AnimatePresence mode="wait">
                        {price ? (
                            <motion.div
                                key={`${price.low}-${price.high}`}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                            >
                                <div className="flex items-baseline gap-2">
                                    <span className="text-3xl xl:text-4xl font-bold text-text-primary tracking-tight">
                                        {price.low.toLocaleString()} – {price.high.toLocaleString()}
                                    </span>
                                    <span className="text-lg text-grey-accent font-medium">DKK</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2 text-xs text-grey-accent">
                                    <svg className="w-4 h-4 text-secondary-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{t.panel.basedOn}</span>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="py-2"
                            >
                                <span className="text-2xl text-gray-300 font-bold">---</span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Scope List */}
                <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                    <p className="text-xs font-bold text-grey-accent uppercase tracking-wider mb-4">{t.panel.projectDetails}</p>

                    {/* Type */}
                    <div className={`transition-all duration-300 ${selectedProject ? 'opacity-100 translate-x-0' : 'opacity-50 translate-x-4 grayscale'}`}>
                        <div className="flex gap-4">
                            <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-xl shadow-sm border border-gray-100">
                                {selectedProject?.icon || <Icons.Clipboard />}
                            </div>
                            <div className="flex-1 py-0.5">
                                <p className="text-[10px] text-grey-accent uppercase font-bold tracking-wide mb-0.5">{t.panel.projectType}</p>
                                <p className="font-semibold text-text-primary text-sm">
                                    {selectedProject?.label || t.panel.notSelected}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Scope */}
                    <div className={`transition-all duration-300 delay-75 ${selectedPurpose ? 'opacity-100 translate-x-0' : 'opacity-40 translate-x-4 grayscale'}`}>
                        <div className="flex gap-4">
                            <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-xl shadow-sm border border-gray-100">
                                <Icons.Target />
                            </div>
                            <div className="flex-1 py-0.5">
                                <p className="text-[10px] text-grey-accent uppercase font-bold tracking-wide mb-0.5">{t.panel.scope}</p>
                                <p className="font-semibold text-text-primary text-sm">
                                    {selectedPurpose?.label || t.panel.pending}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content & Mgmt Summary */}
                    {(answers.content || answers.management) && (
                        <div className="transition-all duration-300 delay-100 animate-in fade-in slide-in-from-right-4">
                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-xl shadow-sm border border-gray-100">
                                    <Icons.Content />
                                </div>
                                <div className="flex-1 py-0.5">
                                    <p className="text-[10px] text-grey-accent uppercase font-bold tracking-wide mb-0.5">{t.panel.setup}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {answers.content && <span className="text-xs bg-gray-100 text-text-primary px-2 py-1 rounded">{contentOptions.find(o => o.id === answers.content)?.label}</span>}
                                        {answers.management && <span className="text-xs bg-gray-100 text-text-primary px-2 py-1 rounded">{managementOptions.find(o => o.id === answers.management)?.label}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Logistics Summary */}
                    {(answers.language || answers.hosting || answers.support) && (
                        <div className="transition-all duration-300 delay-150 animate-in fade-in slide-in-from-right-4">
                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-xl shadow-sm border border-gray-100">
                                    <Icons.Software />
                                </div>
                                <div className="flex-1 py-0.5">
                                    <p className="text-[10px] text-grey-accent uppercase font-bold tracking-wide mb-0.5">{t.panel.features}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {answers.language && <span className="text-xs bg-gray-100 text-text-primary px-2 py-1 rounded">{languageOptions.find(o => o.id === answers.language)?.label}</span>}
                                        {answers.support === 'yes' && <span className="text-xs bg-green-50 text-green-700 border border-green-100 px-2 py-1 rounded">{t.options.supportPlan}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                <div className="mt-8 pt-6 border-t border-[#EDEDED]">
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-text-primary flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">?</span>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-text-primary mb-1">{t.panel.needHelp}</p>
                            <p className="text-xs text-grey-accent leading-relaxed">
                                {t.panel.contactText}
                                <button onClick={() => navigate(`/${lang}#contact`)} className="text-secondary-accent hover:underline ml-1">{t.panel.contactUs}</button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderStepContent = () => {
        switch (step) {
            case 0:
                // Type Selection
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step0.title}
                            </h1>
                            <p className="text-grey-accent">{t.step0.subtitle}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
                            {projectTypes.map((type) => (
                                <OptionCard
                                    key={type.id}
                                    option={type}
                                    selected={answers.projectType === type.id}
                                    onClick={() => handleSelect('projectType', type.id)}
                                />
                            ))}
                        </div>
                    </div>
                );

            case 1:
                // Sub-type Selection
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step1.title}
                            </h1>
                            <p className="text-grey-accent">{t.step1.subtitle}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
                            {otherOptions.map((type) => (
                                <OptionCard
                                    key={type.id}
                                    option={type}
                                    selected={answers.projectType === type.id}
                                    onClick={() => handleSelect('projectType', type.id)}
                                />
                            ))}
                        </div>
                    </div>
                );

            case 2:
                // Purpose
                const purposeOptions = getPurposeOptions();
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step2.title}
                            </h1>
                            <p className="text-grey-accent">{t.step2.subtitle}</p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 max-w-xl mx-auto">
                            {purposeOptions.map((option) => (
                                <OptionCard
                                    key={option.id}
                                    option={{ ...option, icon: <Icons.ArrowRight /> }}
                                    selected={answers.purpose === option.id}
                                    onClick={() => handleSelect('purpose', option.id)}
                                    showDescription={true}
                                />
                            ))}
                        </div>
                    </div>
                );

            case 3:
                // Content & Management
                return (
                    <div className="space-y-8 max-w-2xl mx-auto">
                        <div className="text-center mb-6">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step3.title}
                            </h1>
                            <p className="text-grey-accent">{t.step3.subtitle}</p>
                        </div>

                        {/* Question 1: Content */}
                        <div>
                            <p className="text-sm font-bold text-text-primary mb-3 uppercase tracking-wide">{t.step3.q1}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {contentOptions.map(opt => (
                                    <OptionCard
                                        key={opt.id}
                                        option={opt}
                                        selected={answers.content === opt.id}
                                        onClick={() => handleSelect('content', opt.id)}
                                        showDescription={true}
                                        compact={true}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Question 2: Management (Hidden for Mobile Apps) */}
                        {answers.projectType !== 'mobile' && (
                            <div>
                                <p className="text-sm font-bold text-text-primary mb-3 uppercase tracking-wide">{t.step3.q2}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {managementOptions.map(opt => (
                                        <OptionCard
                                            key={opt.id}
                                            option={opt}
                                            selected={answers.management === opt.id}
                                            onClick={() => handleSelect('management', opt.id)}
                                            showDescription={true}
                                            compact={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 4:
                // Features & Logistics
                const hostingOpts = getHostingOptions(answers.projectType);
                return (
                    <div className="space-y-8 max-w-2xl mx-auto">
                        <div className="text-center mb-6">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step4.title}
                            </h1>
                            <p className="text-grey-accent">{t.step4.subtitle}</p>
                        </div>

                        {/* Languages */}
                        <div>
                            <p className="text-sm font-bold text-text-primary mb-3 uppercase tracking-wide">{t.step4.q1}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {languageOptions.map(opt => (
                                    <OptionCard
                                        key={opt.id}
                                        option={opt}
                                        selected={answers.language === opt.id}
                                        onClick={() => handleSelect('language', opt.id)}
                                        compact={true}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Hosting */}
                        <div>
                            <p className="text-sm font-bold text-text-primary mb-3 uppercase tracking-wide">
                                {answers.projectType === 'mobile' ? t.step4.q2mobile : t.step4.q2base}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {hostingOpts.map(opt => (
                                    <OptionCard
                                        key={opt.id}
                                        option={opt}
                                        selected={answers.hosting === opt.id}
                                        onClick={() => handleSelect('hosting', opt.id)}
                                        showDescription={true}
                                        compact={true}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Support */}
                        <div>
                            <p className="text-sm font-bold text-text-primary mb-3 uppercase tracking-wide">{t.step4.q3}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {supportOptions.map(opt => (
                                    <OptionCard
                                        key={opt.id}
                                        option={opt}
                                        selected={answers.support === opt.id}
                                        onClick={() => handleSelect('support', opt.id)}
                                        showDescription={true}
                                        compact={true}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 5:
                // Optional Description
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step5.title}
                            </h1>
                            <p className="text-grey-accent">{t.step5.subtitle}</p>
                        </div>
                        <div className="max-w-xl mx-auto">
                            <textarea
                                value={answers.description}
                                onChange={(e) => setAnswers({ ...answers, description: e.target.value })}
                                placeholder={t.step5.placeholder}
                                className="w-full h-48 p-4 border-2 border-gray-200 rounded-xl focus:border-secondary-accent focus:outline-none transition-colors resize-none"
                            ></textarea>
                            {/* Skip button removed from here, integrated into Nav Bar */}
                        </div>
                    </div>
                );

            case 6:
                // Contact
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                                {t.step6.title}
                            </h1>
                            <p className="text-grey-accent">{t.step6.subtitle}</p>
                        </div>
                        <div className="max-w-md mx-auto space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-primary mb-2">
                                    {t.step6.businessLabel}
                                </label>
                                <input
                                    type="text"
                                    value={answers.businessName}
                                    onChange={(e) => setAnswers({ ...answers, businessName: e.target.value })}
                                    placeholder={t.step6.businessPlaceholder}
                                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-secondary-accent focus:outline-none transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-primary mb-2">
                                    {t.step6.emailLabel}
                                </label>
                                <input
                                    type="email"
                                    value={answers.email}
                                    onChange={(e) => setAnswers({ ...answers, email: e.target.value })}
                                    placeholder={t.step6.emailPlaceholder}
                                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-secondary-accent focus:outline-none transition-colors"
                                />
                                <p className="text-grey-accent text-xs mt-2">
                                    {t.step6.disclaimer(answers.projectType)}
                                </p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="bg-white min-h-screen flex flex-col pt-[72px] lg:pt-0">
            {/* Navbars */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 lg:static lg:border-none lg:bg-transparent">
                <div className="lg:hidden">
                    <Navbar />
                </div>
            </div>
            <div className="hidden lg:block fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
                <Navbar />
            </div>

            <main className="flex-1 relative lg:flex lg:h-[calc(100vh-80px)] lg:mt-[80px] lg:overflow-hidden">
                {/* Left Column */}
                <div className="flex-1 lg:overflow-y-auto lg:h-full relative bg-white scrollbar-hide">
                    <section className="px-4 py-8 lg:px-12 lg:py-12 max-w-3xl mx-auto min-h-screen lg:min-h-0 pb-32 lg:pb-20">
                        {/* Progress */}
                        <div className="mb-12">
                            <div className="flex gap-2 mb-3">
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div
                                        key={i}
                                        className={`flex-1 h-1 rounded-full transition-all duration-500 ${i <= stepInfo.current ? 'bg-secondary-accent' : 'bg-gray-100'}`}
                                    />
                                ))}
                            </div>
                            <div className="flex justify-between text-xs font-medium text-grey-accent uppercase tracking-wider">
                                <span>{t.nav.start}</span>
                                <span>{step >= 7 ? t.nav.complete : `${t.nav.step} ${stepInfo.current}/6`}</span>
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="min-h-[400px]"
                            >
                                {step >= 7 ? (
                                    // Result Page Logic
                                    (() => {
                                        const price = calculatePrice();
                                        const selectedProject = getSelectedProject();
                                        const purposeOptions = getPurposeOptions();
                                        const selectedPurpose = purposeOptions.find(p => p.id === answers.purpose);
                                        const selectedContent = contentOptions.find(o => o.id === answers.content);
                                        const selectedManagement = managementOptions.find(o => o.id === answers.management);
                                        const selectedLanguage = languageOptions.find(o => o.id === answers.language);
                                        const selectedSupport = supportOptions.find(o => o.id === answers.support);
                                        const selectedHosting = getHostingOptions(answers.projectType).find(o => o.id === answers.hosting);

                                        return (
                                            <div className="max-w-2xl mx-auto pt-4">
                                                <motion.div
                                                    initial={{ scale: 0.9, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-sm"
                                                >
                                                    <Icons.Party />
                                                </motion.div>

                                                <div className="text-center mb-10">
                                                    <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-4 tracking-tight">
                                                        {t.result.title}
                                                    </h2>
                                                    <p className="text-grey-accent text-lg max-w-lg mx-auto">
                                                        {t.result.subtitle(answers.businessName, selectedProject?.label)}
                                                    </p>
                                                </div>

                                                {/* Price Card */}
                                                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 overflow-hidden mb-10">
                                                    <div className="bg-gray-50 border-b border-gray-100 p-6 text-center">
                                                        <p className="text-sm text-grey-accent uppercase tracking-wider font-bold mb-2">{t.panel.estInvestment}</p>
                                                        <div className="flex items-center justify-center gap-2">
                                                            <span className="text-4xl lg:text-5xl font-bold text-text-primary">
                                                                {price.low.toLocaleString()} – {price.high.toLocaleString()}
                                                            </span>
                                                            <span className="text-xl text-grey-accent font-medium mt-2">DKK</span>
                                                        </div>
                                                    </div>

                                                    <div className="p-6 lg:p-8">
                                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-6">What's included</h3>

                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8">
                                                            {/* Project Type */}
                                                            <div className="flex gap-4">
                                                                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                                                    {selectedProject?.icon}
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 font-semibold uppercase mb-0.5">{t.panel.projectType}</p>
                                                                    <p className="font-medium text-gray-900">{selectedProject?.label}</p>
                                                                </div>
                                                            </div>

                                                            {/* Scope */}
                                                            <div className="flex gap-4">
                                                                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                                                                    <Icons.Target />
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 font-semibold uppercase mb-0.5">{t.panel.scope}</p>
                                                                    <p className="font-medium text-gray-900">{selectedPurpose?.label}</p>
                                                                </div>
                                                            </div>

                                                            {/* Setup */}
                                                            <div className="flex gap-4">
                                                                <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                                                                    <Icons.Content />
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 font-semibold uppercase mb-0.5">{t.panel.setup}</p>
                                                                    <p className="font-medium text-gray-900 text-sm">
                                                                        {selectedContent?.label}
                                                                        {selectedManagement && <span className="text-gray-400"> + </span>}
                                                                        {selectedManagement?.label}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            {/* Features */}
                                                            <div className="flex gap-4">
                                                                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                                                                    <Icons.Software />
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 font-semibold uppercase mb-0.5">{t.panel.features}</p>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        <span className="text-sm font-medium text-gray-900">{selectedLanguage?.label}</span>
                                                                        <span className="text-gray-300">•</span>
                                                                        <span className="text-sm font-medium text-gray-900">{selectedHosting?.label}</span>
                                                                        {selectedSupport?.id !== 'no' && (
                                                                            <>
                                                                                <span className="text-gray-300">•</span>
                                                                                <span className="text-sm font-medium text-gray-900">{t.options.supportPlan}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-4 max-w-sm mx-auto">
                                                    <motion.button
                                                        onClick={() => navigate(`/${lang}#contact`)}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        className="w-full px-8 py-4 bg-secondary-accent text-white rounded-xl font-bold hover:bg-secondary-accent/90 transition-all duration-300 shadow-lg shadow-secondary-accent/20 flex items-center justify-center gap-2"
                                                    >
                                                        <span>{t.result.bookBtn}</span>
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                        </svg>
                                                    </motion.button>

                                                    <button
                                                        onClick={() => {
                                                            setStep(0);
                                                            setAnswers({
                                                                projectType: null, purpose: null,
                                                                content: null, management: null,
                                                                language: null, hosting: null,
                                                                support: null, description: '',
                                                                businessName: '', email: ''
                                                            });
                                                        }}
                                                        className="text-grey-accent hover:text-text-primary text-sm font-medium py-2"
                                                    >
                                                        {t.result.startOver}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })()
                                ) : (
                                    renderStepContent()
                                )}

                                {/* Nav Buttons */}
                                {step < 7 && (
                                    <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-100">
                                        <button
                                            onClick={prevStep}
                                            className={`flex items-center gap-2 text-grey-accent hover:text-text-primary transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 ${step === 0 ? 'invisible' : ''}`}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                            {t.nav.back}
                                        </button>

                                        <button
                                            onClick={nextStep}
                                            disabled={!canProceed()}
                                            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-bold transition-all shadow-sm ${canProceed()
                                                ? 'bg-text-primary text-white hover:bg-gray-800 transform hover:-translate-y-0.5'
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                }`}
                                        >
                                            {(() => {
                                                if (step === 6) return t.nav.seeEstimate;
                                                if (step === 5 && !answers.description.trim()) return t.step5.skip;
                                                return t.nav.continue;
                                            })()}
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </section>
                </div>

                {/* Desktop Sidebar */}
                <div className="hidden lg:block w-[400px] xl:w-[480px] border-l border-gray-100 bg-[#FAFAFA]">
                    {renderEstimatePanel(false)}
                </div>
            </main>

            {/* Mobile Bottom Bar */}
            {step < 7 && renderEstimatePanel(true)}

            <div className="lg:hidden">
                <Footer />
            </div>
        </div>
    );
};

export default PriceCalculator;
